/**
 * Format a subscription price for display.
 *
 * One place for a rule that had drifted into five copies (PassCard,
 * MembershipCard, MembershipCardGradient, and the two admin summary cards),
 * three of them character-identical and all of them disagreeing about what an
 * absent price means.
 *
 * The three outcomes:
 *   - **unknown** — no price to show. `'—'`, matching the admin convention for
 *     an absent scalar. Notably includes `''`: an empty admin price field means
 *     "inherit the template price", and `Number('') === 0` is *finite*, so a
 *     naive parse silently renders it as a zero price.
 *   - **zero** — genuinely $0. A comped pass, an imported pass, or a free plan.
 *     Rendered with {@link ZERO_PRICE_LABEL} (see below).
 *   - **paid** — a real amount.
 *
 * `isPaid` exists because the membership cards suppress their billing-frequency
 * suffix ("/mo") when a plan is zero-cost or unknown; without it they would have
 * to compare the returned text against the zero label.
 */

/**
 * Zero-price label for CATALOG / browse surfaces (a template being browsed or
 * selected). "Free" reads better than "$0.00" in a marketing list. This is the
 * default when no `zeroLabel` is passed.
 */
export const ZERO_PRICE_LABEL = 'Free';

/**
 * Zero-price label for a MEMBER'S OWN pass/membership (instance surfaces — "My
 * Plan", the admin summary cards, a comped/discounted assignment). Showing the
 * exact "$0.00" is clearer than "Free" for something a specific member holds.
 *
 * These two are the single place to flip either label — product changes its
 * mind about this. Cards pick the right one from their existing instance signal
 * (PassCard's `price` prop, MembershipCard's `variant="my-plan"`); the admin
 * summary cards pass it explicitly.
 */
export const INSTANCE_ZERO_PRICE_LABEL = '$0.00';

export interface FormattedSubscriptionPrice {
  /** Display text: `'—'`, the zero label, or `'$25.50'`. */
  text: string;
  /** True only for a real non-zero amount — drives suffix/label decisions. */
  isPaid: boolean;
}

const UNKNOWN_PRICE_TEXT = '—';

interface FormatSubscriptionPriceOptions {
  /** Override the $0 label for this call. Defaults to {@link ZERO_PRICE_LABEL}. */
  zeroLabel?: string;
}

/**
 * @param price Dollars, as a decimal string or number. `null`/`undefined`/`''`
 *   and anything non-numeric are all "unknown" — never silently zero.
 */
export function formatSubscriptionPrice(
  price: string | number | null | undefined,
  { zeroLabel = ZERO_PRICE_LABEL }: FormatSubscriptionPriceOptions = {},
): FormattedSubscriptionPrice {
  if (price === null || price === undefined) {
    return { text: UNKNOWN_PRICE_TEXT, isPaid: false };
  }

  // Guard '' before Number(): Number('') === 0 would render a zero price.
  if (typeof price === 'string' && price.trim() === '') {
    return { text: UNKNOWN_PRICE_TEXT, isPaid: false };
  }

  const parsed = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(parsed)) {
    return { text: UNKNOWN_PRICE_TEXT, isPaid: false };
  }

  if (parsed === 0) {
    return { text: zeroLabel, isPaid: false };
  }

  return { text: `$${parsed.toFixed(2)}`, isPaid: true };
}
